<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { isDraggableNode } from './treeOps';

const props = defineProps({
  node: { type: Object, required: true },
  selectedId: { type: String, default: null },
  insertTargetId: { type: String, default: null },
  draggingId: { type: String, default: null },
  dropTargetId: { type: String, default: null },
});
const emit = defineEmits(['select', 'requestInsert']);
const { t } = useI18n();

const isBranching = computed(
  () =>
    ['QuestionStep', 'BranchStep'].includes(props.node.customType) ||
    (props.node.customType === 'HTTPRequest' && props.node.data?.failureBranch)
);
const isContainer = computed(() =>
  ['AnswerStep', 'ConditionStep'].includes(props.node.customType)
);
const isSelected = computed(() => props.node.id === props.selectedId);
const isInsertTarget = computed(() => props.node.id === props.insertTargetId);
const canDrag = computed(() => isDraggableNode(props.node));
const isDragging = computed(() => props.node.id === props.draggingId);
const isDropTarget = computed(() => props.node.id === props.dropTargetId);

const ICONS = {
  Trigger: '⚡',
  SendMessage: '💬',
  QuestionStep: '❓',
  BranchStep: '🔀',
  Jump: '🔁',
  AssignTo: '👤',
  WaitStep: '⏱️',
  CloseConversation: '✅',
  CsatStep: '⭐',
  SendAttachment: '📎',
  DateTime: '📅',
  UpdateContactField: '✏️',
  UpdateContactTags: '🏷️',
  UpdateLifecycleStage: '🧭',
  AddComment: '📝',
  OpenConversation: '🔓',
  TriggerByAnotherFlow: '↪️',
  HTTPRequest: '🌐',
  WhatsAppList: '📋',
  AIStep: '✨',
  SetVariable: '🧩',
};

// The header colour carries meaning, so it is grouped by what a step does
// rather than assigned per type: entry points, questions that split the flow,
// steps that end or route it, and everything that just acts.
const ACCENTS = {
  entry: 'bg-n-amber-3 text-n-amber-11 border-n-amber-5',
  ask: 'bg-n-teal-3 text-n-teal-11 border-n-teal-5',
  end: 'bg-n-ruby-3 text-n-ruby-11 border-n-ruby-5',
  act: 'bg-n-blue-3 text-n-blue-11 border-n-blue-5',
  group: 'bg-n-slate-3 text-n-slate-11 border-n-slate-5',
};

const accent = computed(() => {
  const type = props.node.customType;
  if (type === 'Trigger') return ACCENTS.entry;
  if (isContainer.value) return ACCENTS.group;
  if (['QuestionStep', 'BranchStep', 'WhatsAppList'].includes(type))
    return ACCENTS.ask;
  if (['CloseConversation', 'Jump', 'TriggerByAnotherFlow'].includes(type))
    return ACCENTS.end;
  return ACCENTS.act;
});

const icon = computed(() => ICONS[props.node.customType] || '•');

const label = computed(() => {
  const { customType, name } = props.node;
  if (customType === 'Trigger')
    return t(`TELETEL_FLOWS.TRIGGERS.${props.node.data?.type}`) || name;
  if (isContainer.value) return name;
  return t(`TELETEL_FLOWS.BUILDER.STEP.${customType}`);
});

const preview = computed(() => {
  const { customType, data } = props.node;
  if (customType === 'SendMessage')
    return data?.payload?.[0]?.message?.text || '';
  if (customType === 'QuestionStep') return data?.question?.text || '';
  if (customType === 'Jump')
    return `→ ${data?.stepId || '?'} (×${data?.maxJumps})`;
  if (customType === 'WaitStep') return `${data?.value} ${data?.unit}`;
  if (customType === 'UpdateLifecycleStage') {
    if (data?.clear)
      return t('TELETEL_FLOWS.BUILDER.FORM.LIFECYCLE_CLEAR_STAGE');
    const stage = data?.stageId
      ? t('TELETEL_FLOWS.BUILDER.FORM.LIFECYCLE_STAGE_ID', {
          id: data.stageId,
        })
      : t('TELETEL_FLOWS.BUILDER.FORM.LIFECYCLE_PICK_STAGE');
    return stage;
  }
  if (customType === 'AssignTo') return data?.assignTo || '';
  if (customType === 'AddComment') return data?.text || '';
  if (customType === 'UpdateContactField') return data?.key || '';
  if (customType === 'SetVariable') return data?.key || '';
  if (customType === 'WhatsAppList') return data?.body || data?.title || '';
  return '';
});
</script>

<script>
export default { name: 'FlowNode' };
</script>

<template>
  <div class="flex flex-col items-center">
    <!--
      A div, not a button: form controls cannot start a drag, and dragging is
      driven from the Builder by pointer events keyed off data-node-id.
    -->
    <div
      class="overflow-hidden text-sm border shadow-sm rounded-xl w-56 text-start"
      :class="[
        isDropTarget
          ? 'border-n-brand ring-2 ring-n-brand'
          : isSelected
            ? 'border-n-brand ring-1 ring-n-brand'
            : 'border-n-weak hover:border-n-slate-7',
        isDragging ? 'opacity-40' : '',
        canDrag ? 'cursor-grab active:cursor-grabbing' : 'cursor-pointer',
      ]"
      :data-node-id="canDrag ? node.id : null"
      :data-drop-id="node.id"
      role="button"
      tabindex="0"
      :aria-pressed="isSelected"
      @click.stop="emit('select', node.id)"
      @keydown.enter.stop="emit('select', node.id)"
      @keydown.space.prevent.stop="emit('select', node.id)"
    >
      <div
        class="flex items-center gap-2 px-3 py-2 font-medium border-b"
        :class="accent"
      >
        <span aria-hidden="true">{{ icon }}</span>
        <span class="truncate">{{ label }}</span>
      </div>
      <div
        v-if="preview"
        class="px-3 py-2 text-xs bg-n-background text-n-slate-11 line-clamp-3"
      >
        {{ preview }}
      </div>
    </div>

    <!-- branching nodes: named branch containers side by side -->
    <div v-if="isBranching" class="flex items-start gap-6 pt-4">
      <div
        v-for="child in node.childs"
        :key="child.id"
        class="flex flex-col items-center border-t-2 border-dashed border-n-weak pt-2"
      >
        <span class="mb-1 text-xs font-medium text-n-slate-10">{{
          child.name
        }}</span>
        <FlowNode
          :node="child"
          :selected-id="selectedId"
          :insert-target-id="insertTargetId"
          :dragging-id="draggingId"
          :drop-target-id="dropTargetId"
          @select="emit('select', $event)"
          @request-insert="emit('requestInsert', $event)"
        />
      </div>
    </div>

    <!-- linear nodes and containers: connector + insert + single child -->
    <template v-else>
      <div class="w-px h-3 bg-n-weak" />
      <button
        type="button"
        class="text-sm leading-none border rounded-full transition-all"
        :style="draggingId ? { width: '9rem', height: '2.25rem' } : undefined"
        :class="[
          isDropTarget
            ? 'border-n-brand bg-n-brand/20 text-n-brand scale-150'
            : isInsertTarget
              ? 'border-n-brand text-n-brand bg-n-brand/10'
              : 'border-n-weak text-n-slate-10 hover:border-n-brand hover:text-n-brand',
          draggingId ? 'border-dashed' : '',
        ]"
        :title="
          draggingId
            ? t('TELETEL_FLOWS.BUILDER.DROP_HERE')
            : t('TELETEL_FLOWS.BUILDER.ADD_HERE')
        "
        :data-drop-id="node.id"
        @click.stop="emit('requestInsert', node.id)"
      >
        {{ isInsertTarget ? t('TELETEL_FLOWS.BUILDER.ADD_NEXT_STEP') : '+' }}
      </button>
      <template v-if="node.childs?.length">
        <div class="w-px h-3 bg-n-weak" />
        <FlowNode
          :node="node.childs[0]"
          :selected-id="selectedId"
          :insert-target-id="insertTargetId"
          :dragging-id="draggingId"
          :drop-target-id="dropTargetId"
          @select="emit('select', $event)"
          @request-insert="emit('requestInsert', $event)"
        />
      </template>
    </template>
  </div>
</template>
